import { useMemo } from 'react'
import { ItemRestriction } from 'typings'
import { RuleStack } from 'ui'
import { RegularEffect, RegularScope } from '../../../../../../typings'
import { SCOPE_OPTIONS } from '../../../../../ItemsView/typing'
import { EMPTY_SCOPE_RESTRICTION } from '../constants'
import { RegularScopeEmptyView } from './Empty'
import {
  BrandsRule,
  CategoriesRule,
  CollectionsRule,
  ProductsRule,
  SKUsRule,
} from './Rules'

const scopeRuleMap = {
  [SCOPE_OPTIONS.SKUS]: SKUsRule,
  [SCOPE_OPTIONS.COLLECTIONS]: CollectionsRule,
  [SCOPE_OPTIONS.PRODUCTS]: ProductsRule,
  [SCOPE_OPTIONS.CATEGORIES]: CategoriesRule,
  [SCOPE_OPTIONS.BRANDS]: BrandsRule,
}

export type ScopeRuleProps = {
  config: ItemRestriction
  onEdit: () => void
  onRemove: (field: SCOPE_OPTIONS) => void
}

type ScopeProps = ScopeRuleProps & {
  id: SCOPE_OPTIONS
}

const ScopeRule = (props: ScopeProps) => {
  const { id, config, onEdit, onRemove } = props
  const Component = scopeRuleMap[id]

  if (Component)
    return <Component config={config} onEdit={onEdit} onRemove={onRemove} />

  return null
}

type Props = {
  name: string
  config: RegularScope
  onAdd: (option?: SCOPE_OPTIONS) => void
  onRemove: (config: RegularEffect['scope']) => void
}

export function ItemsView(props: Props) {
  const { name, config, onRemove, onAdd } = props

  const hasItems = useMemo(
    () => Object.values(SCOPE_OPTIONS).some((field) => !!config[field]),
    [config]
  )

  const handleRemove = (type: SCOPE_OPTIONS) => {
    const newConfig: RegularEffect['scope'] = {
      ...config,
      [type]: undefined,
    }

    if (
      Object.values(SCOPE_OPTIONS).every(
        (field) => newConfig[field] === undefined
      )
    ) {
      onRemove(undefined)
    } else {
      onRemove({ ...newConfig })
    }
  }

  return (
    <>
      {hasItems ? (
        <>
          <RuleStack>
            {Object.values(SCOPE_OPTIONS)
              .filter((field) => !!config[field])
              .map((field) => (
                <ScopeRule
                  id={field}
                  key={field}
                  config={config[field]!}
                  onRemove={handleRemove}
                  onEdit={() => onAdd(field)}
                />
              ))}
          </RuleStack>
        </>
      ) : (
        <RegularScopeEmptyView name={name} onAdd={onAdd} labeled={false} />
      )}
    </>
  )
}
